import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from 'lucide-react';

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Headquarters',
      details: ['123 Logistics Way', 'Belleville, ON K8N 5V5', 'Canada']
    },
    {
      icon: Phone,
      title: 'Phone',
      details: ['+1 (613) 555-0123', 'Toll Free: 1-800-555-0123']
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@quintefreight.com', 'quotes@quintefreight.com']
    },
    {
      icon: Clock,
      title: 'Hours',
      details: ['Mon - Fri: 7:00 AM - 6:00 PM EST', '24/7 Dispatch Available']
    }
  ];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-12 rounded-xl text-center max-w-lg shadow-2xl border border-forest/10 relative z-10"
        >
          <div className="w-20 h-20 bg-forest rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-amber" />
          </div>
          <h2 className="font-display font-black text-3xl text-forest mb-4">Message Sent!</h2>
          <p className="font-body text-ink/70 mb-8">
            Thank you for reaching out. Our team will get back to you within 24 business hours.
          </p>
          <a href="/" className="btn-primary">Return Home</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-20" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4"
          >
            Get In Touch
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-black text-display-lg text-forest mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-ink/70 max-w-2xl mx-auto"
          >
            Ready to move your freight? Reach out to our team for quotes, questions, or to discuss your logistics needs.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-8 lg:p-12 rounded-2xl shadow-xl border border-forest/10"
          >
            <h2 className="font-display font-bold text-2xl text-forest mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-ink/80 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
                <div>
                  <label className="block font-body text-ink/80 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body text-ink/80 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
                <div>
                  <label className="block font-body text-ink/80 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block font-body text-ink/80 mb-2">Message *</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your freight needs..."
                  className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-3 text-forest placeholder-forest/40 focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all resize-none"
                />
              </div>
              <button type="submit" className="btn-primary w-full">
                Send Message
                <Send className="w-5 h-5" />
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            {contactInfo.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.1 }}
                className="bg-white p-6 rounded-xl border border-forest/10 flex items-start gap-4"
              >
                <div className="w-12 h-12 bg-forest rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-amber" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-forest mb-1">{item.title}</h3>
                  {item.details.map((detail, j) => (
                    <p key={j} className="font-body text-ink/70">{detail}</p>
                  ))}
                </div>
              </motion.div>
            ))}

            {/* Quick Quote CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-forest p-6 rounded-xl text-center"
            >
              <h3 className="font-display font-bold text-white mb-2">Need a Quote Fast?</h3>
              <p className="font-body text-white/70 mb-4">Get your custom freight quote in minutes</p>
              <a href="/quote" className="btn-primary inline-flex">Get a Quote</a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
